import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGavel, FaSyncAlt, FaTimesCircle, FaUsers, FaClock, FaPlay, FaPause, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

interface AuctionStats {
  total: number;
  waiting: number;
  active: number;
  paused: number;
  completed: number;
}

const AUCTION_STATES = ['all', 'waiting', 'active', 'paused', 'completed'] as const;

const Auctions: React.FC = () => {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState<any[]>([]);
  const [stats, setStats] = useState<AuctionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [seriesFilter, setSeriesFilter] = useState<string>('');
  const [seriesList, setSeriesList] = useState<any[]>([]);

  const loadStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const data = await apiService.getAuctionStats();
      setStats(data);
    } catch (err: any) {
      console.error('Error loading auction stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadAuctions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const filters: { state?: string; seriesId?: string } = {};
      if (stateFilter && stateFilter !== 'all') filters.state = stateFilter;
      if (seriesFilter) filters.seriesId = seriesFilter;

      const data = await apiService.getAuctions(filters);
      const auctionList = Array.isArray(data) ? data : data.auctions || data.data || [];
      setAuctions(auctionList);
    } catch (err: any) {
      console.error('Error loading auctions:', err);
      setError(err?.message || 'Failed to load auctions');
      toast.error(err?.message || 'Failed to load auctions');
    } finally {
      setLoading(false);
    }
  }, [stateFilter, seriesFilter]);

  const loadSeries = useCallback(async () => {
    try {
      const data = await apiService.getSeries();
      const list = Array.isArray(data) ? data : data?.data || [];
      setSeriesList(list);
    } catch (err: any) {
      console.error('Error loading series:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadSeries();
  }, [loadStats, loadSeries]);

  useEffect(() => {
    loadAuctions();
  }, [loadAuctions]);

  const handleRefresh = () => {
    loadStats();
    loadAuctions();
  };

  const getStateColor = (state: string) => {
    switch (state?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 dark:bg-success-500/20 text-emerald-700 dark:text-success-400 border-emerald-200 dark:border-success-500/30';
      case 'waiting':
        return 'bg-amber-100 dark:bg-warning-500/20 text-amber-700 dark:text-warning-400 border-amber-200 dark:border-warning-500/30';
      case 'paused':
        return 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30';
      case 'completed':
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
      default:
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  const statCards = [
    { label: 'Total', value: stats?.total ?? 0, icon: FaGavel, color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-500/10', border: 'border-indigo-200 dark:border-indigo-500/20' },
    { label: 'Waiting', value: stats?.waiting ?? 0, icon: FaHourglassHalf, color: 'text-amber-600 dark:text-warning-400', bg: 'bg-amber-50 dark:bg-warning-500/10', border: 'border-amber-200 dark:border-warning-500/20' },
    { label: 'Active', value: stats?.active ?? 0, icon: FaPlay, color: 'text-emerald-600 dark:text-success-400', bg: 'bg-emerald-50 dark:bg-success-500/10', border: 'border-emerald-200 dark:border-success-500/20' },
    { label: 'Paused', value: stats?.paused ?? 0, icon: FaPause, color: 'text-blue-600 dark:text-primary-400', bg: 'bg-blue-50 dark:bg-primary-500/10', border: 'border-blue-200 dark:border-primary-500/20' },
    { label: 'Completed', value: stats?.completed ?? 0, icon: FaCheckCircle, color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-500/10', border: 'border-gray-200 dark:border-gray-500/20' },
  ];

  if (loading && auctions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading auctions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg border border-indigo-200 dark:border-indigo-500/30">
            <FaGavel className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Auctions</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Monitor auction sessions and lot progress
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
        >
          <FaSyncAlt className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={`${card.bg} border ${card.border} rounded-xl p-4 transition-all`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`h-5 w-5 ${card.color}`} />
                {loadingStats && (
                  <div className="h-4 w-4 border-2 border-gray-300 dark:border-gray-600 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">State</label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-dark-500 border border-gray-300 dark:border-dark-300 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {AUCTION_STATES.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Series</label>
            <select
              value={seriesFilter}
              onChange={(e) => setSeriesFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-dark-500 border border-gray-300 dark:border-dark-300 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Series</option>
              {seriesList.map((s: any) => (
                <option key={s.id || s._id} value={s.id || s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      )}

      {/* Auction List */}
      <div className="space-y-4">
        {auctions.length === 0 && !error ? (
          <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm p-12 text-center">
            <FaGavel className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Auctions Found</h3>
            <p className="text-gray-600 dark:text-gray-400">No auctions match the current filters.</p>
          </div>
        ) : (
          auctions.map((auction: any) => (
            <button
              key={auction.id || auction._id}
              onClick={() => navigate(`/dashboard/auctions/${auction.id || auction._id}`)}
              className="w-full text-left bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm hover:shadow-md dark:shadow-lg transition-all p-5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="p-2 bg-indigo-50 dark:bg-indigo-500/10 rounded-lg border border-indigo-100 dark:border-indigo-500/20 flex-shrink-0">
                    <FaGavel className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">
                        {auction.name || auction.title || 'Auction'}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStateColor(auction.state || auction.status)}`}>
                        {auction.state || auction.status || 'unknown'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {auction.seriesName && <span>{auction.seriesName}</span>}
                      {auction.createdAt && (
                        <span className="flex items-center gap-1">
                          <FaClock className="h-3 w-3" />
                          {new Date(auction.createdAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary badges */}
                <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                  {auction.totalLots != null && (
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{auction.totalLots}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Lots</p>
                    </div>
                  )}
                  {auction.soldLots != null && (
                    <div className="text-center">
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{auction.soldLots}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">Sold</p>
                    </div>
                  )}
                  {(auction.participantCount != null || auction.teamCount != null) && (
                    <div className="text-center flex items-center gap-1">
                      <FaUsers className="h-3.5 w-3.5 text-gray-400" />
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{auction.participantCount ?? auction.teamCount ?? 0}</p>
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default Auctions;
